import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { AdminService } from 'src/app/business-service/admin/admin.service';
import { ClientAdminService } from 'src/app/business-service/client-admin/client-admin.service';
import { ONBOARDING_METHODS, ORGANIZATION_TYPES, RECORD_STATUS_VALUES } from 'src/app/modules/shared/constants/api-constants';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { ModalsService } from 'src/app/modules/shared/services/modals/modals.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { InputValidationsService } from 'src/app/modules/shared/services/validations/input-validations.service';

@Component({
  selector: 'app-user-org-details',
  templateUrl: './user-org-details.component.html',
  styleUrls: ['./user-org-details.component.scss'],
})
export class UserOrgDetailsComponent implements OnInit {

  @Input() organizationId: any;
  @Input() editOrganizationId: any;
  @Input() storedFormData: any;

  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter<any>();
  @Output() assignOrganizationId: EventEmitter<any> = new EventEmitter<any>();
  @Output() submitOrganizationData: EventEmitter<void> = new EventEmitter<void>();

  organizationForm!: FormGroup;
  exisitingUserEmails: any;
  userId: any;
  userDetailsData: any;
  status: string = 'inactive'

  // TODO: use LOV here
  organizationType: any[] = [
    { key: ORGANIZATION_TYPES.AGENT, name: ORGANIZATION_TYPES.AGENT },
    { key: ORGANIZATION_TYPES.DMC, name: ORGANIZATION_TYPES.DMC },
    { key: ORGANIZATION_TYPES.AGENT_AND_DMC, name: ORGANIZATION_TYPES.AGENT_AND_DMC }
  ]

  // TODO: use LOV here
  domainProvidersLov: any[] = [
    { key: "GoDaddy", name: "GoDaddy" }
  ]

  minimumUsers: number = 1;

  @Input() isUserEditing: boolean = false;

  constructor(
    private _fb: FormBuilder,
    private _validationService: InputValidationsService,
    private _globalService: GlobalService,
    private _adminService: AdminService,
    private _responseNotificationService: ResponseNotificationService,
    private _clientService: ClientAdminService,
  ) { }

  async ngOnInit() {
    await this.loadExistingUserEmail();

    this.organizationForm = this._fb.group({
      organizationContact: this._fb.group({
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        personalPhone: [null, [Validators.required]],
        email: ['', [Validators.required, this._validationService.emailValidator(), this.emailCheckForUser()]],
        id: [null]
      }),
      organizationDetails: this._fb.group({
        userId: [''],
        name: ['', [Validators.required]],
        organizationType: ['', [Validators.required]],
        onboardingMethod: [ONBOARDING_METHODS.TripMeld, [Validators.required]],
        onBoardDate: [this.today, [Validators.required]],
        totalLicense: [0, [this._validationService.valueNotZeroValidator()]],
        avaliableLicense: [0],
        address1: [''],
        address2: [''],
        groupedAddress: [null],
        phone: [null],
        websiteLink: ['', [this._validationService.validateWebsiteUrl]],
        email: ['', [this._validationService.emailValidator(), Validators.required, this.emailCheckForOrgEmail()]],
        senderAccess: [null],
        communicationEmails: this._fb.array([]),
        tempcommunicationEmails: ['', [this._validationService.emailValidator(), this.emailCheckForCommunicationEmails()]],
      }),
      domainSettings: this._fb.group({
        domainProvider: ['GoDaddy', []],
        domainName: ['', this._validationService.domainNameValidator()]
      }),
      metaSettings: this._fb.group({
        apiKey: ['', []],
        wabaId: ['', []],
        phoneNumberId: ['', []]
      }),
    });

    this.organizationForm.get("organizationDetails.onBoardDate")?.disable();

    this.organizationForm.valueChanges.subscribe(() => {
      this.organizationForm.markAsTouched();
    })

    this.organizationForm.get("domainSettings.domainName")?.valueChanges.subscribe(value => {
      // When domain name changes and is valid, then patch the domain name to organizationDetails.websiteLink and make it disabled
      const domainControl = this.organizationForm.get("domainSettings.domainName");
      const websiteControl = this.organizationForm.get("organizationDetails.websiteLink");
      if (domainControl?.valid && value) {
        let domainName = value.trim();
        websiteControl?.setValue(domainName);
        websiteControl?.disable();
      } else {
        websiteControl?.enable();
      }
    })

    if (this.storedFormData) {
      this.userId = this.storedFormData.organizationDetails.userId;
      this.patchOrganizationData(this.storedFormData);
      if (this.userId) {
        await this.fetchuserdetails(this.userId);
      }
    }

    if (this.isUserEditing) {
      this.organizationForm.get("organizationDetails.name")?.disable();
      this.organizationForm.get("organizationDetails.totalLicense")?.disable();
    }
  }

  ngOnChanges() {
    this.ngOnInit();
  }

  get today() {
    return new Date();
  }

  get communicationEmails(): FormArray {
    return this.organizationForm.get('organizationDetails.communicationEmails') as FormArray;
  }

  // Patch organization stored data
  patchOrganizationData(organizationData: any) {
    this.organizationForm.patchValue({
      organizationContact: {
        firstName: organizationData.userDetails?.firstName,
        lastName: organizationData.userDetails?.lastName,
        personalPhone: organizationData.userDetails?.officePhoneNumber,
        email: organizationData.userDetails?.email,
        id: organizationData.userDetails?.id || null,
      },
      organizationDetails: {
        name: organizationData.organizationDetails?.name,
        organizationType: organizationData.organizationDetails?.organizationType,
        onboardingMethod: organizationData.organizationDetails?.onboardingMethod,
        onBoardDate: organizationData.organizationDetails?.onBoardDate,
        totalLicense: organizationData.organizationDetails?.totalLicense,
        avaliableLicense: organizationData.organizationDetails?.avaliableLicense,
        address1: organizationData.organizationDetails?.address1,
        address2: organizationData.organizationDetails?.address2,
        groupedAddress: {
          country: organizationData.organizationDetails?.country,
          state: organizationData.organizationDetails?.state,
          city: organizationData.organizationDetails?.city,
          zipCode: organizationData.organizationDetails?.zipCode,
        },
        phone: organizationData.organizationDetails?.phone,
        websiteLink: organizationData.organizationDetails?.websiteLink,
        email: organizationData.organizationDetails?.email,
        senderAccess: organizationData.organizationDetails.senderAccess,
        communicationEmails: organizationData.organizationDetails?.communicationEmails,
      },
      domainSettings: {
        domainProvider: organizationData.domainSettings?.domainProvider || "",
        domainName: organizationData.domainSettings?.domainName
      },
      metaSettings: {
        apiKey: organizationData.metaSettings?.apiKey || "",
        wabaId: organizationData.metaSettings?.wabaId || "",
        phoneNumberId: organizationData.metaSettings?.phoneNumberId || ""
      },
    });

    this.minimumUsers = organizationData.organizationDetails?.totalLicense;

    // disable user email field and company email field
    if (organizationData.userDetails?.status === RECORD_STATUS_VALUES.ACTIVE) {
      this.organizationForm.get('organizationContact.email')?.disable();
    }
    if (organizationData.organizationDetails?.senderAccess === "SUCCESS") {
      this.organizationForm.get('organizationDetails.email')?.disable();
    }

    this.organizationForm.get("organizationDetails.organizationType")?.disable();

    this.communicationEmails.clear();

    if (Array.isArray(organizationData?.organizationDetails?.communicationEmails)) {
      organizationData.organizationDetails?.communicationEmails?.forEach((email: string) => {
        this.communicationEmails.push(this._fb.control(email));
      });
    }

  }

  async fetchuserdetails(userId: string) {
    this._clientService.getUser(userId).subscribe(data => {
      this.status = data.userDetails.senderAccess;
      this.userDetailsData = data.userDetails;
    });
  }

  userEmailTabOut(event: any) {
    let email = event.target.value;
    if (!this.organizationForm.get('organizationDetails.email')?.value) {
      this.organizationForm.get('organizationDetails.email')?.setValue(email);
    }
  }

  // Exsiting email vertification validations
  emailCheckForUser(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const userEmail = control.value.trim().toLowerCase();
      const communicationEmails = this.communicationEmails.controls.map(control => control.value?.email?.trim().toLowerCase());
      if (this.editOrganizationId) {
        this.exisitingUserEmails = this.exisitingUserEmails.filter((email: any) => email !== userEmail);
      }
      if (this.exisitingUserEmails?.includes(userEmail) || communicationEmails?.includes(userEmail)) {
        return { emailTaken: true };
      }

      return null;
    };
  }

  emailCheckForCommunicationEmails(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const communicationEmail = control.value.trim().toLowerCase();
      const organizationEmail = this.organizationForm.get('organizationDetails.email')?.value?.trim().toLowerCase();
      const userEmail = this.organizationForm.get('organizationContact.email')?.value?.trim().toLowerCase();

      if (this.exisitingUserEmails.includes(communicationEmail) ||
        communicationEmail === organizationEmail ||
        communicationEmail === userEmail) {
        return { emailTaken: true };
      }

      return null;
    };
  }

  emailCheckForOrgEmail(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const organizationEmail = control.value.trim().toLowerCase();
      const userEmail = this.organizationForm.get('organizationContact.email')?.value?.trim().toLowerCase();
      const communicationEmails = this.communicationEmails.controls.map(control => control.value?.email?.trim().toLowerCase());
      if (this.editOrganizationId) {
        this.exisitingUserEmails = this.exisitingUserEmails.filter((email: any) => email !== organizationEmail);
      }
      if (this.exisitingUserEmails.includes(organizationEmail) || communicationEmails.includes(organizationEmail)) {
        return { emailTaken: true };
      }

      return null;
    };
  }

  async loadExistingUserEmail(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._adminService.getUserEmails().subscribe({
        next: (res: any) => {
          this.exisitingUserEmails = res;
          resolve();
        },
        error: (error: any) => {
          console.error("Error while fetching user emails", error);
          reject(error);
        }
      });
    });
  }

  // Communucation emails
  addEmail(event: any): void {

    const email = event.target.value.trim();
    const emailControl = this.organizationForm.get('organizationDetails.tempcommunicationEmails');
    const organizationEmail = this.organizationForm.get('organizationDetails.email')?.value;

    if (emailControl) {
      emailControl.markAsTouched();
      emailControl.updateValueAndValidity();
    }
    if (email === organizationEmail) {
      emailControl?.setErrors({ emailTaken: true });
      return;
    }
    if (emailControl?.hasError('emailTaken'))
      return
    if (this.communicationEmails.length == 5)
      emailControl?.setErrors({ limitexceded: true }); // Set the duplicate error
    if (email && this.isValidEmail(email) && this.communicationEmails.length < 5) {
      const isDuplicate = this.communicationEmails.controls.some(
        (control: any) => control?.value?.email === email
      );
      if (isDuplicate) {
        emailControl?.setErrors({ duplicate: true }); // Set the duplicate error
        return;
      }
      if (!isDuplicate) {
        const newEmailGroup = this._fb.group({
          email: [email, [Validators.required, Validators.email]],
          senderAccess: [null] // Default empty senderAccess field
        });
        this.communicationEmails.push(newEmailGroup);
        event.target.value = ''; // Clear the input box after adding the email
      }
    }
  }

  getEmailErrorMessage(control: AbstractControl): string {
    if (control.hasError('emailTaken') && control.touched) {
      return '*User already exists with this email';
    }
    if (control.hasError('invalidEmail') && control.touched) {
      return '*email is Invalid';
    }
    if (control.hasError('duplicate') && control.touched) {
      return '*Duplicate emails are not allowed';
    }
    if (control.hasError('limitexceded') && control.touched) {
      return '*Only 5 emails allowed';
    }
    return ''; // Return empty if there's no error
  }

  removeEmail(index: number): void {
    this.communicationEmails.removeAt(index);
  }

  isValidEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  async onSubmit(): Promise<boolean> {
    if (this.organizationForm.valid) {
      try {
        const formData = await this.serializeFormData(this.organizationForm.getRawValue());
        this.organizationForm.markAsPristine();
        this.storeFormData.emit(formData);
        this.submitOrganizationData.emit();
        return true;
      } catch (error) {
        console.error("Error while adding visa info", error);
        return false;
      }
    } else {
      this.organizationForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert("Fill all required fields to save", "Invalid Form");
      return false;
    }
  }

  async serializeFormData(formData: any) {
    const serializedData = {
      organizationDetails: {
        ...formData.organizationDetails,
        onBoardDate: formData.organizationDetails?.onBoardDate,
        onboardingMethod: formData.organizationDetails?.onboardingMethod,
        avaliableLicense: this.storedFormData ? this.storedFormData.organizationDetails?.avaliableLicense + (formData.organizationDetails.totalLicense - this.minimumUsers) : formData.organizationDetails.totalLicense,
        country: formData.organizationDetails?.groupedAddress?.country?.name,
        state: formData.organizationDetails?.groupedAddress?.state,
        city: formData.organizationDetails?.groupedAddress?.city,
        zipCode: formData.organizationDetails?.groupedAddress?.zipCode
      },
      userDetails: {
        ...formData.organizationContact,
        officePhoneNumber: formData.organizationContact?.personalPhone
      },
      domainSettings: {
        ...formData.domainSettings,
        domainName: formData.domainSettings?.domainName?.trim() || ""
      },
      metaSettings: {
        apiKey: formData.metaSettings?.apiKey?.trim() || "",
        wabaId: formData.metaSettings?.wabaId?.trim() || "",
        phoneNumberId: formData.metaSettings?.phoneNumberId?.trim() || ""
      }
    };

    return serializedData;
  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  goPrevious() {
    this.viewChange.emit(true);
  }

  goNext() {
    this.viewChange.emit(false);
  }

  async saveAndNext() {
    if (await this.onSubmit()) {
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.organizationForm.dirty || this.organizationForm.invalid) && this.organizationForm.touched;
  }

}
